import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { MeterSupplyEntry } from '../../api/schema'
import { useMeterSupply } from '../../hooks/useMeterSupply'

/** The supply reading of the meters under the focal asset, by meter uid. */
const MeterSupplyContext = createContext<ReadonlyMap<string, MeterSupplyEntry>>(new Map())

/**
 * One `?subtree=true` call per Equipment tab (spec 2026-10-27-16 §2): the rows
 * of the metering trade read their chip from it. A failing call (outside the
 * caller's contracts scope, say) leaves the rows without chips.
 */
export function MeterSupplyProvider({ org, assetUid, children }: { org: string; assetUid: string; children: ReactNode }) {
  const { data } = useMeterSupply(org, assetUid, { subtree: true })
  const byUid = useMemo(
    () => new Map<string, MeterSupplyEntry>((data?.meters ?? []).map((m) => [m.asset.uid, m])),
    [data],
  )
  return <MeterSupplyContext.Provider value={byUid}>{children}</MeterSupplyContext.Provider>
}

export function useMeterSupplyEntry(meterUid: string): MeterSupplyEntry | undefined {
  return useContext(MeterSupplyContext).get(meterUid)
}
